import { useEffect, useRef } from 'react';
import { useChargeLinesPreviewMutation } from '@features/billing/store/v2-api';
import type { ChargeLinePreviewResponse } from '@store/generated/billing-v2';

interface UsePreviewByEncounter {
  data?: ChargeLinePreviewResponse;
  isLoading: boolean;
  error: unknown;
}

/**
 * Encounter-scoped wrapper around POST /v2/provider/charge-lines/preview.
 *
 * The mutation underneath is `usePostV2ProviderChargeLinesPreviewMutation`.
 * We treat it as a query — re-fires whenever the encounterId or the
 * `refreshKey` changes, and exposes the response as `data` for query-shaped
 * consumers (drawer Receipt section, SettleSheet defaults).
 *
 * The encounter_id form is the new BE ergonomic shorthand; the IDs form
 * (used by payments/useChargeLinesPreview) covers partial-settlement and
 * statement-run flows. Both are mutually exclusive on the request.
 *
 * Pass a `refreshKey` derived from a value that should trigger a re-fetch
 * when the underlying data changes — usually the financial summary's
 * entry-count or last-entry-id for the encounter. Mutations like
 * captureCharges / recordPayment invalidate the financial summary tag
 * automatically; this prop bridges that refetch into the preview, since
 * mutations don't participate in RTK Query's tag-based cache.
 */
export function usePreviewByEncounter(
  encounterId: string | null | undefined,
  refreshKey?: string | number,
): UsePreviewByEncounter {
  const [trigger, { data, isLoading, error }] = useChargeLinesPreviewMutation();

  // Stash the mutation trigger in a ref so it can't appear in the effect
  // deps. RTK Query's mutation tuple gets a fresh function reference every
  // render — listing it directly in deps creates a render → call → state
  // change → re-render loop.
  const triggerRef = useRef(trigger);
  triggerRef.current = trigger;

  useEffect(() => {
    if (!encounterId) return;
    void triggerRef.current({
      chargeLinePreviewRequest: { encounter_id: encounterId },
    });
  }, [encounterId, refreshKey]);

  return { data, isLoading, error };
}
